/**
 * JAISIU-4515 §2 / §4 — the six default fleet roles.
 *
 * The Bible names these roles as the platform default set. Agents and
 * subagents are instances of a role; the role carries skills, tools,
 * and the LLM model. Custom roles may be added; these six must always
 * be visible in the fleet role list.
 */

export const DEFAULT_FLEET_ROLE_IDS = [
  "jasiu-coordinator",
  "planner",
  "designer",
  "implementator",
  "tester",
  "reviewer",
] as const;

export type DefaultFleetRoleId = (typeof DEFAULT_FLEET_ROLE_IDS)[number];

export type FleetRoleAllowedTools = {
  files: boolean;
  terminal: boolean;
  git: boolean;
  browser: boolean;
};

export type FleetRoleInstructionFiles = {
  "AGENTS.md": string;
  "SOUL.md": string;
  "TOOLS.md": string;
};

export type FleetRoleDefinition = {
  roleId: string;
  name: string;
  description: string;
  skills: string[];
  /** Role-level model. Empty means the platform default. */
  model?: string | { primary?: string; fallbacks?: string[] };
  priority: number;
  version: number;
  allowedTools: FleetRoleAllowedTools;
  files: FleetRoleInstructionFiles;
  builtIn: boolean;
};

const CODING_TOOLS: FleetRoleAllowedTools = {
  files: true,
  terminal: true,
  git: true,
  browser: false,
};

const COORDINATOR_TOOLS: FleetRoleAllowedTools = {
  files: true,
  terminal: false,
  git: false,
  browser: false,
};

function instructionFiles(name: string, description: string): FleetRoleInstructionFiles {
  return {
    "AGENTS.md": `# ${name}\n\n## Purpose\n${description}\n\n## Instructions\n- Follow the assigned task and project conventions.\n- Report blockers to Jasiu.\n`,
    "SOUL.md": `# ${name}\n\nStay inside this role's remit. Do not expand scope without a plan update.\n`,
    "TOOLS.md": `# ${name} tools\n\nUse only the tools granted to this role.\n`,
  };
}

export const DEFAULT_FLEET_ROLES: readonly FleetRoleDefinition[] = [
  {
    roleId: "jasiu-coordinator",
    name: "Jasiu — coordinator",
    description:
      "Leads the engagement, picks executors, talks to the user, and prepares the fleet summary.",
    skills: ["coordination", "communication", "summary"],
    priority: 10,
    version: 1,
    allowedTools: COORDINATOR_TOOLS,
    files: instructionFiles(
      "Jasiu",
      "Leads the engagement, picks executors, talks to the user, and prepares the fleet summary.",
    ),
    builtIn: true,
  },
  {
    roleId: "planner",
    name: "Planner",
    description: "Analyses existing code, sets the change scope, and writes the versioned plan.",
    skills: ["planning", "codebase-inspection"],
    priority: 9,
    version: 1,
    allowedTools: { files: true, terminal: false, git: true, browser: false },
    files: instructionFiles(
      "Planner",
      "Analyses existing code, sets the change scope, and writes the versioned plan.",
    ),
    builtIn: true,
  },
  {
    roleId: "designer",
    name: "Designer",
    description: "Designs UI and UX changes against the existing interface and task requirements.",
    skills: ["ui-design", "ux"],
    priority: 8,
    version: 1,
    allowedTools: { files: true, terminal: false, git: false, browser: true },
    files: instructionFiles(
      "Designer",
      "Designs UI and UX changes against the existing interface and task requirements.",
    ),
    builtIn: true,
  },
  {
    roleId: "implementator",
    name: "Implementator",
    description: "Implements the planned changes.",
    skills: ["implementation", "coding"],
    priority: 7,
    version: 1,
    allowedTools: CODING_TOOLS,
    files: instructionFiles("Implementator", "Implements the planned changes."),
    builtIn: true,
  },
  {
    roleId: "tester",
    name: "Tester",
    description: "Checks the solution against the acceptance criteria.",
    skills: ["testing"],
    priority: 6,
    version: 1,
    allowedTools: CODING_TOOLS,
    files: instructionFiles("Tester", "Checks the solution against the acceptance criteria."),
    builtIn: true,
  },
  {
    roleId: "reviewer",
    name: "Reviewer",
    description: "Reviews the result against requirements, design, and quality.",
    skills: ["review"],
    priority: 5,
    version: 1,
    allowedTools: { files: true, terminal: false, git: true, browser: false },
    files: instructionFiles(
      "Reviewer",
      "Reviews the result against requirements, design, and quality.",
    ),
    builtIn: true,
  },
];

const DEFAULT_ROLE_IDS = new Set<string>(DEFAULT_FLEET_ROLE_IDS);

export function isDefaultFleetRoleId(roleId: string): boolean {
  return DEFAULT_ROLE_IDS.has(roleId.trim());
}

export function defaultFleetRoleById(roleId: string): FleetRoleDefinition | undefined {
  const wanted = roleId.trim();
  return DEFAULT_FLEET_ROLES.find((role) => role.roleId === wanted);
}

/**
 * Merge configured workforce roles with the six Bible defaults.
 *
 * Defaults come first so the role list always opens with the platform
 * set. Configured values win on collision. Extra (custom) roles follow.
 */
export function mergeFleetRoles(
  configured: ReadonlyArray<Partial<FleetRoleDefinition> & { roleId: string }>,
): FleetRoleDefinition[] {
  const byId = new Map<string, Partial<FleetRoleDefinition> & { roleId: string }>();
  for (const role of configured) {
    const id = role.roleId?.trim();
    if (id) {
      byId.set(id, role);
    }
  }
  const defaults = DEFAULT_FLEET_ROLES.map((base) => overlayRole(base, byId.get(base.roleId)));
  const extras = configured
    .filter((role) => {
      const id = role.roleId?.trim();
      return Boolean(id) && !DEFAULT_ROLE_IDS.has(id);
    })
    .map((role) => overlayRole(customRoleStub(role.roleId), role));
  return [...defaults, ...extras];
}

function customRoleStub(roleId: string): FleetRoleDefinition {
  const name = roleId
    .split(/[-_]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
  return {
    roleId,
    name,
    description: "",
    skills: [],
    priority: 1,
    version: 1,
    allowedTools: { files: true, terminal: false, git: false, browser: false },
    files: instructionFiles(name, ""),
    builtIn: false,
  };
}

function overlayRole(
  base: FleetRoleDefinition,
  overlay: (Partial<FleetRoleDefinition> & { roleId: string }) | undefined,
): FleetRoleDefinition {
  if (!overlay) {
    return { ...base, files: { ...base.files }, allowedTools: { ...base.allowedTools } };
  }
  return {
    ...base,
    ...overlay,
    roleId: base.roleId,
    name: overlay.name?.trim() || base.name,
    description: overlay.description?.trim() || base.description,
    skills: Array.isArray(overlay.skills) ? overlay.skills.filter(Boolean) : base.skills,
    model: overlay.model ?? base.model,
    priority: typeof overlay.priority === "number" ? overlay.priority : base.priority,
    version:
      typeof overlay.version === "number" && overlay.version > 0 ? overlay.version : base.version,
    allowedTools: { ...base.allowedTools, ...overlay.allowedTools },
    files: { ...base.files, ...overlay.files },
    builtIn: base.builtIn,
  };
}

export function bumpRoleVersion(version: number | undefined): number {
  const current = typeof version === "number" && version > 0 ? version : 1;
  return current + 1;
}

export function canCreateFleetRole(input: { description?: string; skills?: string[] }): boolean {
  return Boolean(input.description?.trim()) && (input.skills?.length ?? 0) > 0;
}
